import React, { FC, useEffect, useState } from 'react';
import { ContractsFilterOptionsList } from './shared/ContractsFilterOptionsList';
import { ContractsFilterContainer } from './shared/ContractsFilterContainer';
import { useDebouncedValue } from '@mantine/hooks';

export interface AdaptiveFilterOption {
  keyword: string;
  title: string;
}

export interface SelectedItem {
  id: string;
  name: string;
}

export interface AdaptiveFilterProps {
  options?: AdaptiveFilterOption[];
  selectedValues: string[] | SelectedItem[];
  onChange: (values: string[] | SelectedItem[]) => void;
  onReset?: () => void;
  onApply?: () => void;
  searchPlaceholder?: string;
  singleSelect?: boolean;
  showSearch?: boolean;
  serverSearch?: boolean;
  onServerSearchChange?: (search: string) => void;
  isFetchingTypes?: boolean;
  hasNextPage?: boolean;
  isFetchingMore?: boolean;
  onLoadMore?: () => void;
  storeFullObject?: boolean;
  isLoadingCategorys?: boolean;
}

export const ContractsAdaptiveFilter: FC<AdaptiveFilterProps> = ({
  options,
  selectedValues,
  onChange,
  searchPlaceholder,
  singleSelect = false,
  showSearch = true,
  serverSearch,
  onServerSearchChange,
  isFetchingTypes,
  hasNextPage,
  isFetchingMore,
  onLoadMore,
  isLoadingCategorys,
  storeFullObject = false,
}) => {
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch] = useDebouncedValue(searchInput.trim(), 300);

  useEffect(() => {
    if (!serverSearch) return;
    onServerSearchChange?.(debouncedSearch);
  }, [debouncedSearch, onServerSearchChange, serverSearch]);

  const mappedOptions =
    options
      ?.filter(
        (option: AdaptiveFilterOption) =>
          option.title != null && String(option.title).trim() !== '',
      )
      .map((option: AdaptiveFilterOption) => ({
        value: option.keyword,
        label: option.title,
      })) ?? [];

  return (
    <ContractsFilterContainer>
      <ContractsFilterOptionsList
        options={mappedOptions}
        selectedValues={selectedValues}
        onChange={onChange}
        searchPlaceholder={searchPlaceholder}
        singleSelect={singleSelect}
        showSearch={showSearch}
        searchValue={searchInput}
        onSearchValueChange={setSearchInput}
        serverSearch={serverSearch}
        isFetchingTypes={isFetchingTypes}
        hasNextPage={hasNextPage}
        isFetchingMore={isFetchingMore}
        onLoadMore={onLoadMore}
        storeFullObject={storeFullObject}
        isLoadingCategorys={isLoadingCategorys}
      />
    </ContractsFilterContainer>
  );
};
